import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchBattlePass, claimTier } from '../store/battlePassSlice';
import {
  fetchLootboxChestTypes, fetchLootboxKeyTypes, fetchLootboxContents, fetchLootboxInventory,
} from '../../lootbox/store/lootboxSlice';
import ChestInfoModal from '../../lootbox/components/ChestInfoModal';
import KeyInfoModal from '../../lootbox/components/KeyInfoModal';
import DatumPassOfferModal from '../../datumPass/components/DatumPassOfferModal';
import Balance from '../../wallet/components/Balance';
import Preloader from '../../../shared/components/Preloader';
import { launchRewardBurst } from '../../../shared/utils/rewardAnimation';
import { addNewInventoryItems, countInventoryRewards } from '../../inventory/store/inventoryNewSlice';
import '../styles/battlePass.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { entityText } from '../../../i18n/entityText';

import bgImg from '../../../images/datumpass/bg.png';
import crownImg from '../../../images/datumpass/bp_crown.png';
import premBgImg from '../../../images/datumpass/prembg.png';
import freeBgImg from '../../../images/datumpass/freebg.png';
import freePassTableImg from '../../../images/datumpass/freepasstable.png';
import premPassTableImg from '../../../images/datumpass/prembasstablewtext.png';
import ribbonPImg from '../../../images/datumpass/ribbon-p.png';
import ribbonDImg from '../../../images/datumpass/ribbon-d.png';
import rombImg from '../../../images/datumpass/romb.png';
import rombSImg from '../../../images/datumpass/romb-s.png';
import lockImg from '../../../images/datumpass/lock.png';
import lockOpenImg from '../../../images/datumpass/Lock_Open_Icon.png';
import shiningImg from '../../../images/datumpass/shining.png';
import premPrizeBgImg from '../../../images/datumpass/prem-prize-bg.png';
import normalPrizeBgImg from '../../../images/datumpass/normal-prize-bg.png';

const COIN_ICON = '/slider/coins_1.png';
const TON_ICON = '/gram.png';

// Gap between the sticky header and the tier row we auto-scroll to on open
const AUTO_SCROLL_GAP_PX = 8;

// Полёт награды при клейме (как на экране Achievements): coins/GRAM/tickets летят
// в свою плашку строки балансов, предметы (сундук/ключ/модуль) — в пункт меню Inventory.
function rewardFlight(reward) {
  switch (reward.rewardType) {
    case 'COINS':   return { iconSrc: '/coin_icon.png', target: 'coins' };
    case 'TON':     return { iconSrc: TON_ICON, target: 'ton' };
    case 'TICKETS': return { iconSrc: '/ticket_icon.png', target: 'tickets' };
    case 'CHEST':   return { iconSrc: reward.chestImageUrl || '/slider/chest.png', target: 'inventory' };
    case 'KEY':     return { iconSrc: reward.keyImageUrl || '/slider/key.png', target: 'inventory' };
    case 'CHARACTER_MODULE':
      return { iconSrc: reward.characterModuleImageUrl || '/slider/head.png', target: 'inventory' };
    default:
      return null;
  }
}

// Chest / key rewards open the same info popups as the Inventory → Cases tab
const isLootboxReward = (reward) => reward?.rewardType === 'CHEST' || reward?.rewardType === 'KEY';

export default function BattlePassPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { season, userProgress, tiers, isLoading, claimingKey, error } = useSelector(s => s.battlePass);
  const {
    chestTypes, keyTypes, contentsByChestType, contentsLoaded, typesLoaded, chests, keys,
  } = useSelector(s => s.lootbox);

  useEffect(() => {
    dispatch(fetchBattlePass());
  }, [dispatch]);

  // Lootbox catalogs (for chest/key popups) + inventory (owned counters in the popups).
  // Catalogs are static — load once per session; inventory changes, so refresh on every open.
  useEffect(() => {
    if (!typesLoaded) {
      dispatch(fetchLootboxChestTypes());
      dispatch(fetchLootboxKeyTypes());
    }
    if (!contentsLoaded) dispatch(fetchLootboxContents());
    dispatch(fetchLootboxInventory());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const currentLevel = userProgress?.currentLevel ?? 0;

  // Spins threshold of the tier the player has already passed (0 if no tier passed yet).
  const currentTierSpins = useMemo(() => {
    if (!tiers.length || currentLevel === 0) return 0;
    const tier = tiers.find(t => t.tierNumber === currentLevel);
    return tier?.spinsRequired ?? 0;
  }, [tiers, currentLevel]);

  // Next tier (or last tier if already at max).
  const nextTier = useMemo(() => {
    if (!tiers.length || !userProgress) return null;
    return tiers.find(t => t.tierNumber > currentLevel) || tiers[tiers.length - 1];
  }, [tiers, userProgress, currentLevel]);

  // True when the player has reached the very last tier — progress bar should be full.
  const isMaxLevel = useMemo(() => {
    if (!tiers.length) return false;
    const last = tiers[tiers.length - 1];
    return currentLevel >= last.tierNumber;
  }, [tiers, currentLevel]);

  // Spins accumulated within the current tier (resets to 0 each time a tier is completed).
  const spinsInCurrentTier = useMemo(() => {
    if (!userProgress) return 0;
    return Math.max(0, (userProgress.seasonSpins ?? 0) - currentTierSpins);
  }, [userProgress, currentTierSpins]);

  // Span of the current tier (next threshold − previous threshold).
  const currentTierSpan = useMemo(() => {
    if (!nextTier) return 0;
    if (isMaxLevel) return 0;
    return Math.max(1, nextTier.spinsRequired - currentTierSpins);
  }, [nextTier, currentTierSpins, isMaxLevel]);

  const progressPct = useMemo(() => {
    if (isMaxLevel) return 100;
    if (!currentTierSpan) return 0;
    return Math.min(100, (spinsInCurrentTier / currentTierSpan) * 100);
  }, [spinsInCurrentTier, currentTierSpan, isMaxLevel]);

  const handleClaim = (e, tier, track) => {
    if (claimingKey) return;
    const rewards = (track === 'PREMIUM' ? tier.premiumRewards : tier.freeRewards) || [];
    const flights = rewards.map(rewardFlight).filter(Boolean);
    launchRewardBurst({ startRect: e.currentTarget.getBoundingClientRect(), flights });
    dispatch(claimTier({ tierId: tier.id, track })).then((action) => {
      if (claimTier.rejected.match(action)) return;
      // Chests / keys / modules → "new items" badge on the Inventory menu tab
      dispatch(addNewInventoryItems(countInventoryRewards(rewards, r => r.quantity)));
      // Owned counters in the chest/key popups must include the just-claimed items
      if (rewards.some(isLootboxReward)) dispatch(fetchLootboxInventory());
    });
  };

  const [showDatumPassModal, setShowDatumPassModal] = useState(false);

  // DatumPass bought from this screen → premium rewards become claimable right away
  // (userProgress.hasDatumPass + premiumStatus come from GET /v1/battle-pass, so refetch it).
  const handleDatumPassPurchased = () => {
    dispatch(fetchBattlePass());
  };

  // ── Chest / key info popups (same as Inventory → Cases) ──
  const [infoReward, setInfoReward] = useState(null);

  const chestQtyByType = useMemo(() => {
    const m = {};
    for (const c of chests) m[c.chestTypeId] = c.quantity;
    return m;
  }, [chests]);

  const keyQtyByType = useMemo(() => {
    const m = {};
    for (const k of keys) m[k.keyTypeId] = k.quantity;
    return m;
  }, [keys]);

  // Catalog entry for the tapped reward; falls back to what the reward itself carries
  // so the popup still opens while the catalog is loading.
  const infoChestType = useMemo(() => {
    if (infoReward?.rewardType !== 'CHEST') return null;
    return chestTypes.find(c => c.id === infoReward.chestTypeId)
      || { id: infoReward.chestTypeId, name: t('reward.chest', 'Chest'), imageUrl: infoReward.chestImageUrl, rarity: null };
  }, [infoReward, chestTypes, t]);

  const infoKeyType = useMemo(() => {
    if (infoReward?.rewardType !== 'KEY') return null;
    return keyTypes.find(k => k.id === infoReward.keyTypeId)
      || { id: infoReward.keyTypeId, name: t('reward.key', 'Key'), imageUrl: infoReward.keyImageUrl, rarity: null };
  }, [infoReward, keyTypes, t]);

  const stickyRef = useRef(null);
  const tiersWrapRef = useRef(null);
  const [tiersTopPad, setTiersTopPad] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const stickyBottom = stickyRef.current?.getBoundingClientRect().bottom ?? 0;
      const tiersTop = tiersWrapRef.current?.getBoundingClientRect().top ?? 0;
      // Push content so the first 32px slides behind the sticky header
      setTiersTopPad(Math.max(0, stickyBottom - tiersTop - 32));
    };
    const ro = new ResizeObserver(measure);
    if (stickyRef.current) ro.observe(stickyRef.current);
    measure();
    return () => ro.disconnect();
  }, []);

  // On open: scroll the list so the player's current tier (the last one reached,
  // whose rewards are claimable) sits right under the sticky header. Runs once —
  // later refetches (claims, DatumPass purchase) must not jump the list around.
  const didAutoScrollRef = useRef(false);
  useEffect(() => {
    if (didAutoScrollRef.current || !tiers.length || !userProgress) return;
    const wrap = tiersWrapRef.current;
    const lastTier = tiers[tiers.length - 1].tierNumber;
    const targetTier = Math.min(Math.max(currentLevel, 1), lastTier);
    const row = wrap?.querySelector(`[data-tier="${targetTier}"]`);
    if (!wrap || !row) return;
    didAutoScrollRef.current = true;
    const stickyBottom = stickyRef.current?.getBoundingClientRect().bottom ?? 0;
    const delta = row.getBoundingClientRect().top - stickyBottom - AUTO_SCROLL_GAP_PX;
    if (delta > 0) wrap.scrollTop += delta;
  }, [tiers, userProgress, currentLevel]);

  const countdown = formatCountdown(season?.secondsLeft);
  const hasDatumPass = userProgress?.hasDatumPass;

  return (
    <div className="bp-page">
      {/* Fixed top section — header image bleeds behind notch naturally since top:0 */}
      <div className="bp-sticky-header" ref={stickyRef}>
        {/* Balance floats above the header image, positioned absolute inside the fixed container */}
        <div className="bp-balance-wrap">
          <div className="balance-wrapper">
            <Balance />
          </div>
        </div>

        <div className="bp-header" style={{ backgroundImage: `url(${bgImg})` }}>
          <button className="bp-close" onClick={() => navigate(-1)} aria-label={t('common.close', 'Close')}>✕</button>
          <div className="bp-header-content">
            <div className="bp-season-title">
              <img src={crownImg} alt="" className="bp-crown" />
              <span>
                {season?.name
                  ? entityText(t, 'battle_pass_season', season.id, 'name', season.name)
                  : t('battlepass.season_default', 'Season: 1')}
              </span>
            </div>
            <div className="bp-subtitle">{t('battlepass.subtitle', 'Spin the roulette to get rewards')}</div>
            <div className="bp-countdown">
              <span className="bp-countdown-icon">⏱</span>
              {countdown}
            </div>
          </div>
          <div className="bp-header-fade-bottom" />
        </div>

        {/* Ribbon section — progress bar sits on top of ribbon backgrounds */}
        <div className="bp-ribbon-section">
          <img src={ribbonDImg} alt="" className="bp-ribbon-half bp-ribbon-half--left" />
          <img src={ribbonPImg} alt="" className="bp-ribbon-half bp-ribbon-half--right" />
          <div className="bp-progress-wrap">
            <div className="bp-progress-spin-icon">
              <img src="/loading_spinner_icon.png" alt="" />
            </div>
            <div className="bp-progress-track">
              <div className="bp-progress-fill" style={{ width: `${progressPct}%` }} />
              <span className="bp-progress-text">
                {isMaxLevel
                  ? t('battlepass.max', 'MAX')
                  : `${spinsInCurrentTier} / ${currentTierSpan}`}
              </span>
            </div>
            <div className="bp-level-badge">
              <img src={rombImg} alt="" className="bp-level-badge-bg" />
              <span>{currentLevel}</span>
            </div>
          </div>
        </div>
      </div>

      {showDatumPassModal && (
        <DatumPassOfferModal
          onClose={() => setShowDatumPassModal(false)}
          onPurchased={handleDatumPassPurchased}
        />
      )}

      {/* Chest popup: what's inside + owned counters; "Open" leads to Inventory → Cases */}
      {infoChestType && (
        <ChestInfoModal
          chestType={infoChestType}
          contents={contentsByChestType[infoChestType.id]}
          contentsLoading={!contentsLoaded}
          chestQty={chestQtyByType[infoChestType.id] || 0}
          keyQty={keyQtyByType[infoChestType.keyTypeId] || 0}
          openLabel={t('lootbox.open_in_inventory', 'Open in Inventory')}
          onOpen={() => navigate('/inventory', { state: { mainTab: 'cases' } })}
          onClose={() => setInfoReward(null)}
        />
      )}

      {/* Key popup: what keys are for, owned counter or Buy → Shop / Keys */}
      {infoKeyType && (
        <KeyInfoModal
          keyType={infoKeyType}
          quantity={keyQtyByType[infoKeyType.id] || 0}
          onClose={() => setInfoReward(null)}
        />
      )}

      {/* Scrollable area — pass buttons row scrolls together with tier rows
         and slides under the sticky ribbon section. */}
      <div className="bp-tiers-wrap" ref={tiersWrapRef} style={{ paddingTop: tiersTopPad }}>
        <div className="bp-buttons-row">
          <div className="bp-pass-col bp-pass-col--free" style={{ backgroundImage: `url(${freeBgImg})` }}>
            <div className="bp-free-btn" style={{ backgroundImage: `url(${freePassTableImg})` }}></div>
          </div>
          <div className="bp-pass-center-stub" />
          <div className="bp-pass-col bp-pass-col--premium" style={{ backgroundImage: `url(${premBgImg})` }}>
            <button
              className="bp-premium-btn"
              style={{ backgroundImage: `url(${premPassTableImg})` }}
              onClick={() => setShowDatumPassModal(true)}
            />
          </div>
        </div>
        {isLoading && !tiers.length && <Preloader />}
        {error && <div className="bp-error">{error}</div>}
        {tiers.map((tier) => (
          <TierRow
            key={tier.id}
            tier={tier}
            currentLevel={currentLevel}
            hasDatumPass={hasDatumPass}
            claimingKey={claimingKey}
            onClaim={handleClaim}
            onRewardInfo={setInfoReward}
            nextTierSpins={nextTier?.spinsRequired}
            // Vertical pole of row N runs from diamond N down to diamond N+1:
            // full for passed tiers, partial (= main bar) for the tier in progress, empty beyond.
            poleFillPct={
              tier.tierNumber < currentLevel ? 100
                : tier.tierNumber === currentLevel ? progressPct
                  : 0
            }
          />
        ))}
      </div>
    </div>
  );
}

function TierRow({ tier, currentLevel, hasDatumPass, claimingKey, onClaim, onRewardInfo, nextTierSpins, poleFillPct }) {
  const isClaimingPremium = claimingKey === `${tier.id}|PREMIUM`;
  const isClaimingFree = claimingKey === `${tier.id}|FREE`;
  const isPast = tier.tierNumber <= currentLevel;
  const isCurrent = tier.tierNumber === currentLevel + 1;

  const premReward = tier.premiumRewards?.[0] || null;
  const freeReward = tier.freeRewards?.[0] || null;

  return (
    <div
      className={`bp-tier-row ${isPast ? 'bp-tier-row--past' : ''} ${isCurrent ? 'bp-tier-row--current' : ''}`}
      data-tier={tier.tierNumber}
    >
      <div className="bp-tier-col bp-tier-col--free" style={{ backgroundImage: `url(${freeBgImg})` }}>
        <RewardCard reward={freeReward} status={tier.freeStatus} premium={false} onInfo={onRewardInfo} />
        {tier.freeStatus === 'AVAILABLE' && (
          <button className="bp-claim-btn" onClick={(e) => onClaim(e, tier, 'FREE')} disabled={isClaimingFree}>
            {isClaimingFree ? '…' : i18n.t('common.claim', 'Claim')}
          </button>
        )}
      </div>

      <div className="bp-tier-center">
        <div className="bp-tier-center-fill" style={{ height: `${poleFillPct}%` }} />
        <div className="bp-tier-diamond-wrap">
          <img src={isPast || isCurrent ? rombImg : rombSImg} alt="" className="bp-tier-diamond-bg" />
          <span className="bp-tier-number">{tier.tierNumber}</span>
        </div>
        {0&&isCurrent && (
          <div className="bp-level-up-badge">
            <span className="bp-level-up-text">{i18n.t('battlepass.plus_level', '+1 level')}</span>
            <div className="bp-level-up-cost">
              <img src={COIN_ICON} alt="" className="bp-level-up-coin" />
              <span>{nextTierSpins ?? tier.spinsRequired}</span>
            </div>
          </div>
        )}
      </div>

      <div className="bp-tier-col bp-tier-col--premium" style={{ backgroundImage: `url(${premBgImg})` }}>
        <RewardCard reward={premReward} status={tier.premiumStatus} premium onInfo={onRewardInfo} />
        {tier.premiumStatus === 'AVAILABLE' && hasDatumPass && (
          <button className="bp-claim-btn" onClick={(e) => onClaim(e, tier, 'PREMIUM')} disabled={isClaimingPremium}>
            {isClaimingPremium ? '…' : i18n.t('common.claim', 'Claim')}
          </button>
        )}
      </div>
    </div>
  );
}

function RewardCard({ reward, status, premium, onInfo }) {
  const { t } = useTranslation();
  const locked = status === 'LOCKED';
  const claimed = status === 'CLAIMED';
  const tappable = isLootboxReward(reward);

  let icon = null;
  let label = '';

  if (reward) {
    switch (reward.rewardType) {
      case 'COINS':
        icon = COIN_ICON;
        label = formatNumber(reward.value);
        break;
      case 'TON':
        icon = TON_ICON;
        label = `${reward.value} GRAM`;
        break;
      case 'CHEST':
        icon = reward.chestImageUrl || '/slider/chest.png';
        label = `x${reward.quantity}`;
        break;
      case 'KEY':
        icon = reward.keyImageUrl || '/slider/key.png';
        label = `x${reward.quantity}`;
        break;
      case 'CHARACTER_MODULE':
        icon = reward.characterModuleImageUrl || '/slider/head.png';
        label = `x${reward.quantity}`;
        break;
      default:
        label = `x${reward.quantity ?? 1}`;
    }
  }

  const cardBg = premium ? premPrizeBgImg : normalPrizeBgImg;

  return (
    <div
      className={`bp-reward-card ${premium ? 'bp-reward-card--premium' : 'bp-reward-card--free'} ${locked ? 'bp-reward-card--locked' : ''} ${claimed ? 'bp-reward-card--claimed' : ''} ${tappable ? 'bp-reward-card--tappable' : ''}`}
      style={{ backgroundImage: `url(${cardBg})` }}
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      aria-label={tappable
        ? (reward.rewardType === 'CHEST' ? t('battlepass.chest_info', 'Chest info') : t('battlepass.key_info', 'Key info'))
        : undefined}
      onClick={tappable ? () => onInfo?.(reward) : undefined}
      onKeyDown={tappable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInfo?.(reward); } } : undefined}
    >
      {tappable && <span className="bp-reward-info-hint" aria-hidden="true">i</span>}
      {locked && <img src={lockImg} alt={t('battlepass.locked', 'locked')} className="bp-reward-lock" />}
      {claimed && <img src={lockOpenImg} alt={t('battlepass.received', 'Received')} className="bp-reward-lock-open" />}
      {icon && (
        <div className="bp-reward-icon-wrap">
          {premium && !locked && <img src={shiningImg} alt="" className="bp-reward-shining" />}
          <img src={icon} alt="" className="bp-reward-icon" />
        </div>
      )}
      {reward && (
        <div className="bp-reward-label-wrap">
          <span className={`bp-reward-label ${claimed ? 'bp-reward-label--received' : ''}`}>
            {claimed ? t('battlepass.received', 'Received') : label}
          </span>
          {claimed && <span className="bp-reward-check">✓</span>}
        </div>
      )}
      {!reward && <div className="bp-reward-empty" />}
    </div>
  );
}

function formatCountdown(seconds) {
  const s = !seconds || seconds <= 0 ? 0 : seconds;
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  return i18n.t('duration.d_h', '{{d}}d {{h}}h', { d: days, h: hours });
}

function formatNumber(value) {
  const n = Number(value);
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return Math.round(n / 1000) * 1000 === n ? (n / 1000 | 0) + 'K' : n.toLocaleString();
  return String(n);
}
